import { useState } from "react";
import { Link } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useLang } from "@/i18n";
import { http, errText, SIGN_KEYS } from "@/lib/api";
import { BirthFields, emptyBirth, toPayload } from "@/components/tools/BirthFields";
import { FormError, PageShell } from "@/components/site/PageShell";

const verdict = (t, total) => (total < 18 ? t.milan.verdict.low : total < 25 ? t.milan.verdict.mid : t.milan.verdict.high);

const Partner = ({ t, who, info }) => (
  <p className="text-sm text-[color:var(--muted)]">
    <b className="text-[color:var(--ink)]">{t.milan[who]}:</b> {t.rashifal.signs[SIGN_KEYS[info.moon_sign]]} · {t.names.nakshatras[info.moon_nakshatra.index]}
  </p>
);

const Result = ({ t, data }) => (
  <div className="mt-12 grid lg:grid-cols-12 gap-8" data-testid="milan-result">
    <div className="lg:col-span-5 card p-8">
      <p className="lbl">{t.milan.score}</p>
      <p className="display text-8xl text-[color:var(--saffron)] leading-none" data-testid="milan-total">{data.result.total}</p>
      <p className="mt-2 text-[color:var(--muted)]">{t.milan.of} 36</p>
      <p className="mt-6 leading-relaxed" data-testid="milan-verdict">{verdict(t, data.result.total)}</p>
      <div className="mt-6 space-y-1"><Partner t={t} who="boy" info={data.boy} /><Partner t={t} who="girl" info={data.girl} /></div>
    </div>
    <div className="lg:col-span-7 card overflow-hidden">
      <table className="w-full text-sm">
        <thead><tr className="text-left text-[color:var(--muted)]"><th className="px-6 py-3 font-medium">{t.milan.koota}</th><th className="px-6 py-3 font-medium text-right">{t.milan.points}</th></tr></thead>
        <tbody>
          {data.result.kootas.map((k) => (
            <tr key={k.key} className="border-t border-[color:var(--line)]" data-testid={`koota-${k.key}`}>
              <td className="px-6 py-3">{t.milan.kootas[k.key]}</td>
              <td className="px-6 py-3 text-right font-semibold">{k.score} <span className="text-[color:var(--muted)] font-normal">/ {k.max}</span></td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="px-6 py-4 text-xs text-[color:var(--muted)] border-t border-[color:var(--line)]">{t.milan.note}</p>
    </div>
    <Link to="/book" data-testid="milan-book-btn" className="pill-solid px-7 py-3.5 text-sm w-fit">{t.common.bookCta}</Link>
  </div>
);

export default function MilanPage() {
  const { t } = useLang();
  const [boy, setBoy] = useState(emptyBirth);
  const [girl, setGirl] = useState(emptyBirth);
  const [state, setState] = useState({ loading: false, error: "", data: null });
  const submit = async (e) => {
    e.preventDefault();
    if (!boy.place || !girl.place) { setState({ loading: false, error: t.common.choosePlace, data: null }); return; }
    setState({ loading: true, error: "", data: null });
    try {
      const r = await http.post("/milan", { boy: toPayload(boy), girl: toPayload(girl) });
      setState({ loading: false, error: "", data: r.data });
    } catch (err) {
      setState({ loading: false, error: errText(err, t.common.error), data: null });
    }
  };
  return (
    <PageShell eyebrow={t.milan.eyebrow} title={t.milan.title} desc={t.milan.desc} testid="milan">
      <form onSubmit={submit} className="space-y-6" data-testid="milan-form">
        <div className="grid lg:grid-cols-2 gap-6">
          {[["boy", boy, setBoy], ["girl", girl, setGirl]].map(([who, v, set]) => (
            <div key={who} className="card p-6 sm:p-8">
              <h3 className="display text-3xl mb-5">{t.milan[who]}</h3>
              <BirthFields t={t} value={v} onChange={set} prefix={`milan-${who}`} />
            </div>
          ))}
        </div>
        <FormError msg={state.error} />
        <button type="submit" data-testid="milan-submit" disabled={state.loading} className="pill-solid px-7 py-3.5 text-sm disabled:opacity-60">
          {state.loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}{state.loading ? t.common.calculating : t.milan.match}
        </button>
      </form>
      {state.data && <Result t={t} data={state.data} />}
    </PageShell>
  );
}
